/**
 * Office Categorization Mapping
 * This file contains the mapping of function names to their respective office types.
 */

export const FRONT_OFFICE_FUNCTIONS = [
  'Sales',
  'Marketing',
  'Customer Service',
  'Sales & Customer Service',
  'Customer Success & Account Management',
  'Customer Engagement & Support',
  'Customer Experience',
  'Customer Relations',
  'Customer Order Fulfillment',
  'E-commerce',
  'Sales & Business Development',
  'Sales & Distribution',
  'Sales & Marketing',
  'Sales & Trading',
  'Business Development',
  'Marketing & Brand Management',
  'Marketing & Branding',
  'Commercialization & Marketing',
  'Patient Services',
  'Patient Services (Specialty & Personalized Therapies)',
  'Retail & Commercial Banking',
  'Private Banking',
  'Wealth & Asset Management',
  'Brokerage & Client Services',
  'Investment Banking & Advisory',
  'Investment Banking',
  'Foreign Exchange & Treasury Services',
  'Prime Brokerage Services',
  'Asset Management & Private Wealth Management',
  'Investor Relations',
  'Public Affairs & Government Relations',
  'Store Operations',
  'Merchandising',
  'Product Design',
  'Product Development & Innovation',
  'Product Management',
  'Digital Capabilities',
  'Services',
  'Professional Services & Consulting',
  'Underwriting & Risk Selection',
  'Policy Issuance & Administration',
  'Claims Initiation & First Notice of Loss (FNOL)',
];

export const MIDDLE_OFFICE_FUNCTIONS = [
  'Corporate Strategy',
  'R&D / Product Development',
  'Research and Development',
  'Research & Development',
  'Research & Analysis',
  'Research & Market Analysis',
  'Health Economics and Outcomes Research',
  'Clinical Operations',
  'Lifecycle Management',
  'Quality Assurance',
  'Quality Assurance & Quality Control',
  'Compliance & Regulatory Reporting',
  'Compliance and Risk Management',
  'Compliance Management',
  'Risk Management',
  'Actuarial & Risk Management',
  'Investment & Asset Liability Management (ALM)',
  'Valuation & Product Control',
  'Trade Support & Operations',
  'Treasury & Liquidity Management',
  'Reinsurance & Risk Transfer',
  'Claims Management & Fraud Prevention',
  'Business Intelligence & Analytics',
  'Data Analytics & Business Intelligence',
  'Data Management & Technology',
  'Operations',
  'Operations & Process Optimization',
  'Manufacturing',
  'Manufacturing Execution (MES)',
  'Manufacturing & Supply Chain',
  'Supply Chain',
  'Supply Chain Planning & Execution',
  'Supply Chain & Logistics',
  'Distribution & WMS',
  'Distribution Center Management',
  'Inventory Management',
  'Sourcing & Procurement',
  'Supplier Management',
  'Environmental Health & Safety',
  'Health, Safety, and Environment (HSE)',
  'Sustainability & Safety',
  'Corporate Sustainability',
  'Food Safety',
  'Innovation',
  'Project Management Office (PMO)',
  'Product & Engineering Management',
  'Investment',
  'Contracts & Pricing',
  'Service Assurance',
  'Service Fulfillment',
];

export const BACK_OFFICE_FUNCTIONS = [
  'Billing',
  'Finance ERP',
  'Finance CPM/EPM',
  'Financial Control & Treasury Management',
  'Human Resources (HR)',
  'HR',
  'IT Service Management',
  'IT',
  'Technology & Systems Support',
  'Technology & Cybersecurity',
  'Procurement (Direct)',
  'Procurement (Industrial)',
  'Procurement (Back Office)',
  'Procurement (Indirect)',
  'Legal',
  'Legal / Contract Management',
  'Legal & Contract Management',
  'Support Services',
  'Corporate Strategy',
  'Regulatory Compliance',
  'Regulatory Compliance / GRC',
  'Regulatory Compliance & Governance',
  'Regulatory & Compliance',
];

// Keywords for fallback matching
export const OFFICE_KEYWORDS = {
  FRONT: [
    'sales',
    'distribution',
    'marketing',
    'innovation',
    'underwriting',
    'customer',
    'client',
    'business development',
    'advertising',
  ],
  MIDDLE: [
    'risk',
    'actuarial',
    'claims',
    'fraud',
    'analytics',
    'intelligence',
    'gaming',
    'investment',
    'operations',
    'optimization',
    'reinsurance',
    'cybersecurity',
    'compliance',
    'process',
    'hse',
    'safety',
    'environment',
    'r&d',
    'engineering',
    'regulatory',
  ],
  BACK: [
    'finance',
    'hr',
    'procurement',
    'legal',
    'admin',
    'corporate',
    'it',
    'infrastructure',
    'accounting',
    'resource',
    'epm',
    'cpm',
    'erp',
  ],
};

/**
 * Returns the office rank for a given function name.
 * 1: Front Office
 * 2: Middle Office
 * 3: Back Office
 * 4: Other
 */
export const getOfficeRank = (functionName: string): number => {
  const name = functionName.trim();
  const lowerName = name.toLowerCase();

  // 1. Check exact matches first (case-insensitive for safety)
  if (FRONT_OFFICE_FUNCTIONS.some((f) => f.toLowerCase() === lowerName)) return 1;
  if (MIDDLE_OFFICE_FUNCTIONS.some((f) => f.toLowerCase() === lowerName)) return 2;
  if (BACK_OFFICE_FUNCTIONS.some((f) => f.toLowerCase() === lowerName)) return 3;

  // 2. Fallback to keyword matching
  if (OFFICE_KEYWORDS.FRONT.some((keyword) => lowerName.includes(keyword))) return 1;
  if (OFFICE_KEYWORDS.MIDDLE.some((keyword) => lowerName.includes(keyword))) return 2;
  if (OFFICE_KEYWORDS.BACK.some((keyword) => lowerName.includes(keyword))) return 3;

  return 4;
};
